import React from 'react';
import { Avatar, Box, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import protocolIcon from 'assets/images/ic_monitorization.svg';
import Loader from 'components/SectionLoader';
import MenuIcon from '@mui/icons-material/Menu';
import { formatDurationAbbreviated, formatToDate, formatToTime } from 'utils/format';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';

function AcquisitionRow({
  loadingDoctor,
  loadingPatient,
  acquisition,
  decryptData,
  doctors,
  onClickMenu,
  onClickProtocol,
}) {
  const { t } = useTranslation();
  const anchorRef = React.useRef(null);

  const LOADING = t('messages.loading');

  const protocolId = acquisition.acquisition_id;
  const patientName = acquisition.patient_name;
  const startTime = acquisition.start_time;
  const { duration } = acquisition;
  const { parameters } = acquisition;
  const { status } = acquisition;

  const translateStatus = (statusCode) => {
    switch (statusCode) {
      case 'ST':
        return t('physio.status.ST');
      case 'DG':
        return t('physio.status.DG');
      case 'DO':
        return t('physio.status.DO');
      case 'ES':
        return t('physio.status.ES');
      case 'EF':
        return t('physio.status.EF');
      default:
        return statusCode;
    }
  };

  const decryptPatientName = (encryptName) => {
    if (encryptName === 'Anonymous') {
      return t('physio.physio-personal.table-physio-patient-anonymous');
    }

    if (!decryptData || Object.keys(decryptData).length === 0) {
      return <Loader loading padding="0" width="20px" />;
    }

    const patient = Object.values(decryptData).find(
      (item) => item.nameHash && item.nameHash.trim() === encryptName.trim(),
    );

    if (patient) {
      return patient.name || encryptName || '-';
    }

    return t('physio.physio-personal.table-physio-patient-not-found');
  };

  const getDoctorName = (acquisition) => {
    const doctor = doctors.find((curr) => Number(acquisition.doctor_id) === Number(curr.id)) || {};
    return doctor.full_name || '';
  };

  const getHexColor = (colorName) => {
    const colorMap = {
      lightgreen: '#8EE22C',
      red: '#FF0000',
      grey: '#AEAEAE',
      green: '#33BC6A',
      yellow: '#F9B63E',
      lightyellow: '#FBCB71',
    };

    return colorMap[colorName] || colorName;
  };

  return (
    <TableRow key={protocolId} data-testid={`acquisition-${protocolId}`}>
      <TableCell>
        <Box display="flex" alignItems="center">
          <img src={protocolIcon} alt="protocol icon" className={styles.protocolIcon} />
          <div className={styles.acquisitionLink} onClick={() => onClickProtocol(acquisition)} role="presentation">
            {protocolId}
          </div>
        </Box>
      </TableCell>
      <TableCell>{loadingPatient ? LOADING : decryptPatientName(patientName)}</TableCell>
      <TableCell>{`${formatToDate(new Date(startTime))} ${formatToTime(startTime)}`}</TableCell>
      <TableCell align="left">
        <Box
          sx={{
            backgroundColor: (() => {
              if (!parameters?.ratio) return '#FFFFFF';
              if (parameters?.color_ratio) {
                return `${getHexColor(parameters.color_ratio)}80`;
              }
              return '#80808033';
            })(),
            border: (() => {
              if (!parameters?.ratio) return '2px solid #FFFFFF';
              if (parameters?.color_ratio) {
                return `2px solid ${getHexColor(parameters.color_ratio)}`;
              }
              return '2px solid #808080';
            })(),
            color: '#002f72',
            padding: '2px 6px',
            borderRadius: '40px',
            display: 'inline-block',
            minWidth: '45px',
            textAlign: 'center',
          }}
        >
          {parameters?.ratio || parameters?.ratio === 0 ? parameters.ratio.toFixed(2) : '-'}
        </Box>
      </TableCell>

      <TableCell align="left">
        <Box
          sx={{
            backgroundColor: (() => {
              if (!parameters?.time_to_peak) return '#FFFFFF';
              if (parameters?.color_time_to_peak) {
                return `${getHexColor(parameters.color_time_to_peak)}80`;
              }
              return '#80808033';
            })(),
            border: (() => {
              if (!parameters?.time_to_peak) return '2px solid #FFFFFF';
              if (parameters?.color_time_to_peak) {
                return `2px solid ${getHexColor(parameters.color_time_to_peak)}`;
              }
              return '2px solid #808080';
            })(),
            color: '#002f72',
            padding: '2px 6px',
            borderRadius: '40px',
            display: 'inline-block',
            minWidth: '45px',
            textAlign: 'center',
          }}
        >
          {parameters?.time_to_peak || parameters?.time_to_peak === 0 ? parameters.time_to_peak.toFixed(2) : '-'}
        </Box>
      </TableCell>
      <TableCell>{formatDurationAbbreviated(duration)}</TableCell>
      <TableCell>
        {loadingDoctor ? (
          LOADING
        ) : (
          <Box display="flex" alignItems="center">
            <Avatar sx={{ width: 32, height: 32, margin: '0 8px 0 0' }} />
            <Typography variant="body2" color="secondary">
              {getDoctorName(acquisition)}
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell>{translateStatus(status)}</TableCell>
      <TableCell>
        <div ref={anchorRef}>
          <Loader loading={acquisition.isDownloading} padding="0px" width="16px">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => onClickMenu({ currentTarget: anchorRef.current }, acquisition)}
              data-testid="menu-acquisition"
            >
              <MenuIcon
                sx={{
                  width: '28px',
                  height: '28px',
                }}
                color="primary"
              />
            </IconButton>
          </Loader>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default AcquisitionRow;
